import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';

import { useToast } from '@/components/ui/use-toast';

import { useLogin } from '@/pages/unauthenticated/auth/mutations/use-login';
import {
  DEFAULT_FORM_VALUES,
  LoginFormValues,
  loginFormSchema,
} from '@/pages/unauthenticated/auth/login/form/form-schema';
import { ServiceErrorCode } from '@/services/utils/service-error';

export const useLoginPage = (code?: string, redirectUri?: string) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    reValidateMode: 'onChange',
  });

  const { mutate, isPending: isLoading, isSuccess } = useLogin({
    onSuccess: () => {
      form.clearErrors();
      /**
       * This is a CLI login request.
       */
      if (code && redirectUri) {
        navigate(`/cli?code=${code}&redirectUri=${redirectUri}`);
        return;
      }
      navigate('/dashboard');
    },
    onError: (error) => {
      const { errorCode } = error;

      if (errorCode === ServiceErrorCode.InvalidCredentials) {
        form.setError('email', {
          message: 'Incorrect email or password',
        });
        form.setError('password', {
          message: '',
        });
        return;
      }

      if (errorCode === ServiceErrorCode.UnknownError) {
        toast({
          variant: 'destructive',
          title: 'Uh oh! Something went wrong.',
          description:
            'There was an issue logging in. We are aware of the problem and are looking into it.',
        });
      }
    },
  });

  const onSubmit = async (values: LoginFormValues) => {
    mutate(values);
  };

  return { form, onSubmit, isLoading, isSuccess };
};
